import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { APPEND_TOKEN } from 'src/app/constants/app-constants';
import { AuthService } from 'src/app/core/services/auth.service';
import { TierThemeService } from 'src/app/core/services/tier-theme.service';
import {
  CMSTierData,
  ServerTiersListResponse,
  TierData,
  TierItem,
  TierResponse,
  UserCurrentTier
} from './tiers.model';

@Injectable({
  providedIn: 'root'
})
export class TierService {
  private selectedTierSubject = new BehaviorSubject<TierData | null>(null);
  selectedTier$ = this.selectedTierSubject.asObservable();

  private tierResponseSubject = new BehaviorSubject<TierResponse | null>(null);
  tierResponse$ = this.tierResponseSubject.asObservable();

  private progressSubject = new BehaviorSubject<{
    showProgress: boolean;
  } | null>(null);

  progress$ = this.progressSubject.asObservable();

  readonly tiersColors = ['#A9A9A9', '#D2AD16', '#6E8396'];

  readonly showProgress$ = this.progress$.pipe(
    map((progress) => !!progress?.showProgress),
    distinctUntilChanged()
  );
  private finalTiersData$?: Observable<TierResponse>;

  constructor(
    private httpClient: HttpClient,
    private tierThemeService: TierThemeService,
    private authService: AuthService
  ) {
    // Clear cached tier data on logout so the next user gets fresh data and theme
    this.authService.isLoggedIn$
      .pipe(filter((loggedIn) => !loggedIn))
      .subscribe(() => this.clearCache());
  }

  /**
   * Clear cached tier data. Called on logout so the next user gets fresh API
   * response and correct theme (setThemeByTierLevel) when they load dashboard/tiers.
   */
  clearCache(): void {
    this.finalTiersData$ = undefined;
    this.tierResponseSubject.next(null);
    this.selectedTierSubject.next(null);
  }

  readonly tiersListPayload = {
    limit: 2,
    sortOrder: 'descending',
    sortBy: 'updatedAt',
    searchFilters: [
      {
        filterType: 'is_active',
        values: ['active']
      },
      {
        filterType: 'merchantIds',
        values: ['274d4209-3270-4765-aeed-7c593816b3e2']
      }
    ]
  };

  getTiersList() {
    return this.httpClient.post<ServerTiersListResponse>(
      `${environment.apiHost}/api/user/tiers/get-tiers-list`,
      this.tiersListPayload
    );
  }

  getTiersDataFromCMS(code: string) {
    return this.httpClient.get<CMSTierData>(
      `${environment.cmsHost}/api/tiers/?filters[tierCode][$eq]=${code}&populate=*,tierLevels.banner,tierLevels.rewards,tierLevels.rewards.icon`,
      {
        context: new HttpContext().set(APPEND_TOKEN, false)
      }
    );
  }

  getCurrentTier() {
    return this.httpClient.get<UserCurrentTier>(
      `${environment.apiHost}/api/user/profile/user-current-tier`
    );
  }

  getFinalTiersData() {
    if (this.finalTiersData$) {
      return this.finalTiersData$;
    }
    this.finalTiersData$ = this.getTiersList().pipe(
      switchMap((tiersResponse) => {
        const tierCode = tiersResponse.tiers[0].tierCode;

        return forkJoin({
          cmsData: this.getTiersDataFromCMS(tierCode),
          currentTierData: this.getCurrentTier()
        }).pipe(
          map(({ cmsData, currentTierData }) => {
            const tierLevels = cmsData.data[0]?.attributes.tierLevels || [];

            const currentTierInfo = currentTierData.currentTiers.find(
              (tier) => tier.tierCode === tierCode
            );
            const tierName = currentTierInfo?.tierName || '';
            const daysLeft = Math.max(
              0,
              Math.ceil(
                (new Date(
                  currentTierInfo?.nextTierEvaluationAt || ''
                ).getTime() -
                  new Date().getTime()) /
                  (1000 * 60 * 60 * 24)
              )
            );

            const spendCriteria = currentTierInfo?.achievedTargetPerCriteria[0];

            const tiers: TierData[] = tierLevels.map((level, index) => {
              const items: TierItem[] = level.rewards.map((reward) => ({
                id: reward.id,
                name: reward.title,
                description: reward.description || reward.shortDescription,
                icon: reward.icon.data.attributes.url
              }));

              const currentIndex = tierLevels.findIndex(
                (level) => level.tierLevelName === tierName
              );

              this.tierThemeService.setThemeByTierLevel(currentIndex + 1);

              return {
                name: level.tierLevelName,
                textColor: '#F3F2F1',
                backgroundColor: this.tiersColors[index],
                progressHexCodes: level.progressHexCodes,
                noProgressHexCodes: level.noProgressHexCodes,
                isCurrentTier: tierName === level.tierLevelName,
                items,
                backgroundImage: level.banner.data.attributes.url,
                isLockedTier: index > currentIndex
              };
            });

            // Calculate targetSpend: amount needed to unlock next tier OR stay in current tier (for Platinum)
            const currentValue = Number(spendCriteria?.currentValue) || 0;
            let targetSpend = 0;

            if (
              spendCriteria?.nextTierMilestone !== undefined &&
              spendCriteria.nextTierMilestone !== null
            ) {
              // Has next tier: calculate amount needed to unlock next tier
              targetSpend = Math.max(
                0,
                Number(spendCriteria.nextTierMilestone) - currentValue
              );
            } else if (
              spendCriteria?.tierThreshold !== undefined &&
              spendCriteria.tierThreshold !== null
            ) {
              // No next tier (Platinum): calculate amount needed to maintain current tier
              targetSpend = Math.max(
                0,
                Number(spendCriteria.tierThreshold) - currentValue
              );
            }

            const response: TierResponse = {
              tiers,
              currentSpend: currentValue,
              targetSpend,
              daysLeft,
              nextTierName: currentTierInfo?.nextTierName || '',
              targetDate: new Date(
                currentTierInfo?.nextTierEvaluationAt || ''
              ).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: '2-digit'
              })
            };

            this.tierResponseSubject.next(response);

            return response;
          })
        );
      }),
      shareReplay(1)
    );
    return this.finalTiersData$;
  }

  setSelectedTier(tier: TierData) {
    if (tier.isCurrentTier) {
      this.setShowProgress(true);
    } else {
      this.setShowProgress(false);
    }
    this.selectedTierSubject.next(tier);
  }

  setShowProgress(show: boolean) {
    this.progressSubject.next({ showProgress: show });
  }
}
